import {createTheme} from '@mui/material/styles';
import {alpha} from "@mui/material";

/**
 * Изолированная тема для Профиля Партнера
 *
 * Основана на blueTheme из тестовых страниц:
 * - Primary: #2563eb (синий)
 * - Фон: #f0f2f5 (серо-голубой)
 * - Шрифты: Space Grotesk (заголовки), DM Sans (текст), JetBrains Mono (мелкий текст)
 * - Радиусы: 12px/16px
 * - Тени: мягкие, floating cards style
 */
export const partnerTheme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#2563eb',        // Синий primary
            dark: '#1d4ed8',        // Ховер
            light: 'rgba(37,99,235,0.06)', // Фон для фокуса
            contrastText: '#ffffff',
        },
        secondary: {
            main: '#374151',        // Тёмно-серый
            light: '#9ca3af',
            contrastText: '#ffffff',
        },
        error: {
            main: '#dc2626',
            dark: '#991b1b',
            light: 'rgba(220,38,38,0.06)',
            contrastText: '#ffffff',
        },
        warning: {
            main: '#d97706',
            light: 'rgba(217,119,6,0.08)',
            dark: '#b45309',
        },
        success: {main: '#059669'},
        background: {
            default: '#f0f2f5',     // Фон страницы (серо-голубой)
            paper: '#ffffff',       // Фон карточек
        },
        text: {
            primary: '#111827',     // Основной текст (почти чёрный)
            secondary: '#374151',   // Вторичный (тёмно-серый)
            disabled: '#9ca3af',    // Задизейбленный (серый)
        },
        divider: '#dde1e7',       // Границы
    },
    typography: {
        // Шрифты (подключаем через Google Fonts в index.html)
        fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",

        h1: {fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700},
        h2: {fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700},
        h3: {fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600},
        h4: {fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600},
        h5: {fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600},
        h6: {fontFamily: "'Space Grotesk', sans-serif", fontWeight: 600, fontSize: '1.125rem'},

        button: {
            textTransform: 'none',  // Без капитализации
            fontWeight: 500
        },
        caption: {fontSize: '0.75rem'},
        overline: {
            fontFamily: "'JetBrains Mono', monospace",
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            fontSize: '0.75rem',
        },
    },
    shape: {
        borderRadius: 12,         // Стандартный радиус
    },
    shadows: [
        'none',
        '0 1px 3px rgba(0,0,0,0.04)',     // 1 - Карточка
        '0 1px 3px rgba(0,0,0,0.04)',
        '0 8px 28px rgba(0,0,0,0.08)',    // 3 - Ховер
        '0 8px 28px rgba(0,0,0,0.08)',
        '0 20px 60px rgba(0,0,0,0.18)',   // 5 - Модалка
        ...Array(19).fill('0 20px 60px rgba(0,0,0,0.18)'),
    ] as any,
    components: {
        // Карточки - floating style
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                    border: '1px solid #dde1e7',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    transition: 'box-shadow 0.3s ease, transform 0.2s ease',
                    '&:hover': {
                        boxShadow: '0 8px 28px rgba(0,0,0,0.08)',
                    },
                },
            },
        },
        // Alert - делаем error-сообщения читаемыми без ручных sx
        MuiAlert: {
            styleOverrides: {
                standardError: ({theme}) => ({
                    color: theme.palette.error.dark,
                    backgroundColor: 'rgba(220,38,38,0.08)',
                    border: '1px solid rgba(220,38,38,0.18)',
                    '& .MuiAlert-icon': {
                        color: theme.palette.error.main,
                        opacity: 1,
                    },
                }),
                standardWarning: ({theme}) => ({
                    color: theme.palette.warning.dark,
                    backgroundColor: theme.palette.warning.light,
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.18)}`,
                    '& .MuiAlert-icon': {
                        color: theme.palette.warning.main,
                        opacity: 1,
                    },
                }),
            },
        },
        // Кнопки
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    textTransform: 'none',
                    fontWeight: 500,
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                        transform: 'translateY(-1px)',
                    },
                },
                contained: {
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                    '&:hover': {
                        boxShadow: '0 4px 12px rgba(37,99,235,0.15)',
                    },
                },
                outlined: {
                    borderColor: '#dde1e7',
                    '&:hover': {
                        borderColor: '#2563eb',
                        backgroundColor: 'rgba(37,99,235,0.04)',
                    },
                },
            },
        },
        // Чипы
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 500,
                    fontSize: '0.75rem',
                },
                colorPrimary: {
                    backgroundColor: '#f0effc',
                    color: '#4f46e5',
                },
            },
        },
        // Инпуты
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    transition: 'box-shadow 0.2s ease',
                    '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(37,99,235,0.06)',
                    },
                    '& fieldset': {
                        borderColor: '#dde1e7',
                    },
                    '&:hover fieldset': {
                        borderColor: '#b0b8c4',
                    },
                },
            },
        },
        // Paper
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 16,
                },
                elevation1: {
                    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                },
            },
        },
        // Divider
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: '#dde1e7',
                },
            },
        },
    },
});
